/**
 * Dialog auf dem Handy nach unten wegwischen (01.11.2026).
 *
 * Auf schmalen Bildschirmen (< 640 px) erscheinen Dialoge als Fenster von unten (CSS-Klasse
 * `ew-unten`, index.css). Wer oben am Fenster (Griff bzw. Kopfzeile, obere 72 px) nach unten
 * wischt, zieht es mit; ab 100 px schließt es – wie bei nativen iOS-/Android-Sheets. Geschlossen
 * wird über die Escape-Taste, damit Radix sein normales onOpenChange(false) auslöst und
 * „ungespeicherte Änderungen“-Abfragen der Dialoge weiter greifen.
 */
import { useCallback, useRef } from 'react';

const KOPFZONE_PX = 72;
const SCHWELLE_PX = 100;

export function useWischenSchliessen(aktiv = true) {
  const aufraeumen = useRef(null);

  return useCallback((el) => {
    if (aufraeumen.current) { aufraeumen.current(); aufraeumen.current = null; }
    if (!el || !aktiv || typeof window === 'undefined') return;

    let startY = null;
    let dy = 0;

    const zuruecksetzen = () => {
      el.style.transition = 'transform .2s ease-out';
      el.style.transform = '';
      setTimeout(() => { el.style.transition = ''; }, 220);
    };

    const onStart = (e) => {
      if (window.innerWidth >= 640 || e.touches.length !== 1) return;
      const r = el.getBoundingClientRect();
      const y = e.touches[0].clientY;
      if (y - r.top > KOPFZONE_PX || el.scrollTop > 0) return;
      if (e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      startY = y;
      dy = 0;
    };
    const onMove = (e) => {
      if (startY == null) return;
      dy = Math.max(0, e.touches[0].clientY - startY);
      if (dy > 4) {
        el.style.transition = 'none';
        el.style.transform = `translateY(${dy}px)`;
      }
    };
    const onEnd = () => {
      if (startY == null) return;
      startY = null;
      if (dy > SCHWELLE_PX) {
        el.style.transition = 'transform .16s ease-in';
        el.style.transform = 'translateY(100%)';
        setTimeout(() => {
          // Schließ-Animation abschalten – das Fenster ist schon unten, sonst spränge es kurz zurück
          el.style.animation = 'none';
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
          // Bleibt der Dialog offen (z. B. Rückfrage „Änderungen verwerfen?“), zurückschnappen
          setTimeout(() => { if (el.isConnected && el.dataset.state === 'open') { el.style.animation = ''; zuruecksetzen(); } }, 60);
        }, 150);
      } else if (dy > 0) {
        zuruecksetzen();
      }
      dy = 0;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    aufraeumen.current = () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [aktiv]);
}

/** Mehrere Refs (forwardRef + Callback-Ref) zusammenführen. */
export function refsVerbinden(...refs) {
  return (el) => {
    refs.forEach((r) => {
      if (typeof r === 'function') r(el);
      else if (r) r.current = el;
    });
  };
}
